<script setup>
import { onMounted, reactive, ref } from "vue";
import { apiQuery } from "@/api/j24api";

const loading = ref(true);
const unknownVersions = reactive([]);

const headers = [
  { text: "App Name", value: "appname", sortable: true },
  { text: "Installed Version", value: "installedversion" },
  { text: "No. Devices", value: "devices" },
  { text: "Intel Latest", value: "intelLatest" },
  { text: "Intel Previous", value: "intelPrevious" },
  { text: "Intel Status", value: "IntelStatus" },
  { text: "AS Latest", value: "asLatest" },
  { text: "AS Previous", value: "asPrevious" },
  { text: "AS Status", value: "asStatus" },
];

const searchValue = ref("");
function searchFields() {
  function searchFields() {
    var result = [];
    headers.forEach((header) => {
      if (header.text != "Actions") result.push(header.text);
    });
    return result;
  }
}

function processData(result) {
  unknownVersions.length = 0;
  if (!result.reportData) {
    return;
  }
  const patches = result.patchData;
  result.reportData.forEach((version) => {
    const patch = patches.filter((p) => p.appName == version.appname);
    //console.log("Filtered Patch", patch);
    patch.forEach((p) => {
      if (p.stream == "Intel") {
        version.intelPatch = p;
        version.intelLatest = p.currentVersion;
        version.intelPrevious = p.previousVersion;

        version.intelStatus = analyzeVersion(
          version.intelLatest,
          version.intelPrevious,
          version.installedversion,
          p
        );

        version.intelStatusIcon = chooseIcon(version.intelStatus);
        version.intelStatusToolTip = chooseToolTipText(version.intelStatus);
        version.intelStatusDescription = chooseStatusDescription(
          version.intelStatus
        );
      }
      if (p.stream == "AppleSilicon") {
        version.asPatch = p;
        version.asLatest = p.currentVersion;
        version.asPrevious = p.previousVersion;

        version.asStatus = analyzeVersion(
          version.asLatest,
          version.asPrevious,
          version.installedversion,
          p
        );

        version.asStatusIcon = chooseIcon(version.asStatus);
        version.asStatusToolTip = chooseToolTipText(version.asStatus);
        version.asStatusDescription = chooseStatusDescription(version.asStatus);
      }
    });
    unknownVersions.push(version);
  });
}

function chooseIcon(status) {
  var response = {
    icon: "",
    colour: "",
  };
  switch (status) {
    case "OkToAdd":
      response.icon = "mdi-plus-box";
      break;
    case "NewerThanPackaged":
      response.icon = "mdi-alert";
      break;
    case "NotSemantic":
      response.icon = "mdi-head-question";
      break;
    case "NewerThanPrevious":
      response.icon = "mdi-fan-alert";
      break;
    case "InDefinitionApproved":
      response.icon = "mdi-check";
      response.colour = "success";
      break;
    case "InDefinition":
      response.icon = "mdi-check";
      response.colour = "warning";
      break;
    default:
      response.icon = "mdi-alert";
  }
  return response;
}

function chooseToolTipText(status) {
  switch (status) {
    case "OkToAdd":
      return "Patch AI thinks this version is ok to add";
    case "NewerThanPackaged":
      return "Newer than packaged";
    case "NotSemantic":
      return "Not a semantic version";
    case "NewerThanPrevious":
      return "Warning! Newer than previous";
    case "InDefinitionApproved":
      return "Version is in definition and approved. No action required";
    case "InDefinition":
      return "Version is in definition but not approved yet";
    default:
      return "Unknown";
  }
}

function chooseStatusDescription(status) {
  switch (status) {
    case "OkToAdd":
      return "Patch AI thinks this version is ok to add";
    case "NewerThanPackaged":
      return "EXTREME CAUTION! Newer than packaged this may downgrade devices to an older version";
    case "NotSemantic":
      return "Not a semantic version, manual decision required";
    case "NewerThanPrevious":
      return "EXTREME CAUTION! This could downgrade devices in patching delay to an older version";
    case "InDefinitionApproved":
      return "Version is in definition and approved. No action required";
    case "InDefinition":
      return "Version is in definition but not approved yet";
    default:
      return "Unknown";
  }
}

function analyzeVersion(latestVersion, previousVersion, newVersion, patch) {
  // console.log(
  //   "Analyze Version",
  //   latestVersion,
  //   previousVersion,
  //   newVersion,
  //   patch
  // );
  // Check if version is in the definition already
  if (patch.patches?.length) {
    let matchingPatch = patch.patches.find((p) => p.version == newVersion);
    // console.log("Matching Patch", matchingPatch);
    if (matchingPatch) {
      if (matchingPatch.approved) {
        return "InDefinitionApproved";
      }
      return "InDefinition";
    }
  }
  // Check if all versions are semantic versions
  if (
    !isSemanticVersion(latestVersion) ||
    !isSemanticVersion(previousVersion) ||
    !isSemanticVersion(newVersion)
  ) {
    return "NotSemantic";
  }

  // Compare new version with the latest version
  const comparisonWithLatest = compareSemanticVersions(
    newVersion,
    latestVersion
  );
  if (comparisonWithLatest === "newer") {
    return "NewerThanPackaged";
  }

  // Compare new version with both the latest and previous versions
  const comparisonWithPrevious = compareSemanticVersions(
    newVersion,
    previousVersion
  );
  if (comparisonWithLatest === "older" && comparisonWithPrevious === "older") {
    return "OkToAdd";
  }

  if (comparisonWithLatest === "older" && comparisonWithPrevious === "newer") {
    return "NewerThanPrevious";
  }

  // If none of the above conditions are met, return "Unknown"
  return "Unknown";
}

onMounted(async () => {
  const result = await apiQuery("ELA", "patching/patchingVersions", {
    apiAction: "getUnknownVersionsReport",
  });
  console.log(result);
  processData(result);
  loading.value = false;
});

function isSemanticVersion(versionString) {
  // This regex matches strings that start with standard semantic versioning
  // and allows for additional numeric segments separated by dots.
  const regex = /^(\d+)\.(\d+)\.(\d+)(\.\d+)*$/;
  return regex.test(versionString);
}

function compareSemanticVersions(version1, version2) {
  const parts1 = version1.split(".").map(Number);
  const parts2 = version2.split(".").map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0; // Default to 0 if undefined
    const part2 = parts2[i] || 0; // Default to 0 if undefined

    if (part1 > part2) return "newer";
    if (part1 < part2) return "older";
  }
  // If all components are equal, then the versions are considered equal.
  return "equal";
}

const dialog = ref(false);
const selected = ref({});

function openDialog(item) {
  selected.value = item;
  disableASAddBtn.value = false;
  confirmASAdd.value = false;
  asAddedVersion.value = false;
  disableIntelAddBtn.value = false;
  confirmIntelAdd.value = false;
  intelAddedVersion.value = false;
  confirmASAddVersionLoading.value = false;
  confirmIntelAddVersionLoading.value = false;
  dialog.value = true;
}

const alerts = reactive([]);
const disableIntelAddBtn = ref(false);
const confirmIntelAdd = ref(false);
const confirmIntelAddVersionLoading = ref(false);
const intelAddedVersion = ref(false);
const disableASAddBtn = ref(false);
const confirmASAdd = ref(false);
const confirmASAddVersionLoading = ref(false);
const asAddedVersion = ref(false);

function clickAddIntel() {
  disableIntelAddBtn.value = true;
  confirmIntelAdd.value = true;
}

function clickAddAS() {
  disableASAddBtn.value = true;
  confirmASAdd.value = true;
}

async function confirmIntelAddVersion() {
  console.log("Confirm Intel Add Version");
  confirmIntelAddVersionLoading.value = true;
  const result = await addManualVersion(
    selected.value.intelPatch._id,
    selected.value.installedversion
  );
  if (result) {
    intelAddedVersion.value = true;
  } else {
    alerts.push("Failed to add Intel Version");
  }
  confirmIntelAddVersionLoading.value = false;
  confirmIntelAdd.value = false;
  disableIntelAddBtn.value = false;
}

async function confirmASAddVersion() {
  console.log("Confirm AS Add Version");
  confirmASAddVersionLoading.value = true;
  const result = await addManualVersion(
    selected.value.asPatch._id,
    selected.value.installedversion
  );
  if (result) {
    asAddedVersion.value = true;
  } else {
    alerts.push({
      type: "error",
      message: "Failed to add Apple Silicon Version",
    });
  }
  confirmASAddVersionLoading.value = false;
  confirmASAdd.value = false;
  disableASAddBtn.value = false;
}

async function addManualVersion(patchId, newVersion) {
  try {
    const result = await apiQuery("ELA", "patching/patchingVersions", {
      apiAction: "AddManualVersion",
      newVersion: {
        _id: patchId,
        version: newVersion,
      },
    });
    if (result.updatedRecord) {
      return true;
    }
    console.log(result);
    return false;
  } catch (error) {
    console.log(error);
    return false;
  }
}
</script>

<template>
  <v-dialog v-model="dialog" max-width="900">
    <v-alert v-for="alert in alerts" :key="alert" closable type="error">{{
      alert
    }}</v-alert>
    <v-card>
      <v-card-title>
        <v-row>
          <v-spacer></v-spacer>
          <v-col cols="auto" class="text-center">
            <h2>{{ selected.appname }}</h2>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto">
            <v-icon @click="dialog = false"> mdi-close </v-icon>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-spacer></v-spacer>
        </v-row>
        <v-row>
          <v-col cols="6">
            <v-row>
              <v-col>
                <h2>Intel</h2>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-icon :color="selected.intelStatusIcon.colour">{{
                  selected.intelStatusIcon.icon
                }}</v-icon
                >{{ selected.intelStatusDescription }}
              </v-col>
            </v-row>
            <v-row v-if="!intelAddedVersion">
              <v-col>
                <v-btn
                  @click="clickAddIntel"
                  :disabled="disableIntelAddBtn"
                  v-if="!selected.intelStatus.includes('InDefinition')"
                  >Add Unknown Version</v-btn
                >
                <v-btn
                  v-if="confirmIntelAdd"
                  @click="confirmIntelAddVersion"
                  :loading="confirmIntelAddVersionLoading"
                  >Are you sure?</v-btn
                >
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col> Version Added Successfully </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  label="Version Detected"
                  v-model="selected.installedversion"
                  readonly
                  density="compact"
                ></v-text-field>
                <v-text-field
                  label="Latest Version"
                  v-model="selected.intelLatest"
                  readonly
                  density="compact"
                ></v-text-field>
                <v-text-field
                  label="Previous Version"
                  v-model="selected.intelPrevious"
                  readonly
                  density="compact"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="6">
            <v-row>
              <v-col>
                <h2>Apple Silicon</h2>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-icon :color="selected.asStatusIcon.colour">{{
                  selected.asStatusIcon.icon
                }}</v-icon
                >{{ selected.asStatusDescription }}
              </v-col>
            </v-row>
            <v-row v-if="!asAddedVersion">
              <v-col>
                <v-btn
                  v-if="!selected.asStatus.includes('InDefinition')"
                  @click="clickAddAS"
                  :disabled="disableASAddBtn"
                  >Add Unknown Version</v-btn
                >
                <v-btn
                  v-if="confirmASAdd"
                  @click="confirmASAddVersion"
                  :loading="confirmASAddVersionLoading"
                  >Are you sure?</v-btn
                >
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col> Version Added Successfully </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  label="Version Detected"
                  v-model="selected.installedversion"
                  readonly
                  density="compact"
                ></v-text-field>
                <v-text-field
                  label="Latest Version"
                  v-model="selected.asLatest"
                  readonly
                  density="compact"
                ></v-text-field>
                <v-text-field
                  label="Previous Version"
                  v-model="selected.asPrevious"
                  readonly
                  density="compact"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>

  <v-row>
    <v-col>
      <v-text-field
        variant="underlined"
        density="compact"
        label="Search..."
        hide-details
        v-model="searchValue"
      ></v-text-field>
    </v-col>
    <v-spacer></v-spacer>
  </v-row>
  <v-row>
    <v-col cols="12">
      <EasyDataTable
        :headers="headers"
        :items="unknownVersions"
        :loading="loading"
        :search-field="searchFields()"
        :search-value="searchValue"
        @clickRow="openDialog"
        alternating
        theme-color="#e7541e"
      >
        <template #item-IntelStatus="item">
          <v-btn
            v-if="item.intelStatusIcon"
            icon
            variant="text"
            density="compact"
          >
            <v-icon :color="item.intelStatusIcon.colour"
              >{{
                item.intelStatusIcon.icon
                  ? item.intelStatusIcon.icon
                  : "mdi-alert"
              }}
            </v-icon>
            <v-tooltip
              activator="parent"
              location="start"
              :text="
                item.intelStatusToolTip ? item.intelStatusToolTip : 'Unknown'
              "
            ></v-tooltip>
          </v-btn>
        </template>

        <template #item-asStatus="item">
          <v-btn v-if="item.asStatusIcon" icon variant="text" density="compact">
            <v-icon :color="item.asStatusIcon.colour"
              >{{
                item.asStatusIcon.icon ? item.asStatusIcon.icon : "mdi-alert"
              }}
            </v-icon>
            <v-tooltip
              activator="parent"
              location="start"
              :text="item.asStatusToolTip ? item.asStatusToolTip : 'Unknown'"
            ></v-tooltip>
          </v-btn>
        </template>
      </EasyDataTable>
    </v-col>
  </v-row>
</template>
